export type Player = 'me' | 'opponent';
export type Color = 'w' | 'b';

export type EvalScore = {
  kind: 'cp' | 'mate';
  val: number;
};

export type Classification = {
  inaccuracy: number;
  mistake: number;
  blunder: number;
};

export type PvLine = {
  san: string[];
  scoreCp: number;
};

const TH = {
  inaccuracy_min: 75,
  mistake_min: 125,
  blunder_min: 250,
  winning_score_max: 500,
  winning_score_min: -500,
  inaccuracy_pct_500_1000: 20,
  inaccuracy_pct_gt_1000: 25,
  mistake_pct_500_1000: 30,
  mistake_pct_gt_1000: 40,
  blunder_pct_500_1000: 40,
  blunder_pct_gt_1000: 50,
};

const ZERO_CLASSIFICATION: Classification = {
  inaccuracy: 0,
  mistake: 0,
  blunder: 0,
};

export function dynThreshold(
  base: number,
  pct5: number,
  pctGt: number,
  lastScore: number,
): number {
  const prev = Math.abs(lastScore);
  if (prev > 500 && prev <= 1000) {
    return Math.trunc(prev * (pct5 / 100));
  }
  if (prev > 1000) {
    return Math.trunc(prev * (pctGt / 100));
  }
  return base;
}

export function classify(opts: {
  player: Player;
  score: number;
  scoreChange: number;
  lastScore: number;
}): Classification {
  const guard =
    (opts.player === 'me' &&
      opts.score < TH.winning_score_max &&
      opts.scoreChange < 0) ||
    (opts.player !== 'me' &&
      opts.score > TH.winning_score_min &&
      opts.scoreChange > 0);
  if (!guard) {
    return {...ZERO_CLASSIFICATION};
  }

  const absChange = Math.abs(opts.scoreChange);
  if (
    absChange >
    dynThreshold(
      TH.blunder_min,
      TH.blunder_pct_500_1000,
      TH.blunder_pct_gt_1000,
      opts.lastScore,
    )
  ) {
    return {inaccuracy: 0, mistake: 0, blunder: 1};
  }
  if (
    absChange >
    dynThreshold(
      TH.mistake_min,
      TH.mistake_pct_500_1000,
      TH.mistake_pct_gt_1000,
      opts.lastScore,
    )
  ) {
    return {inaccuracy: 0, mistake: 1, blunder: 0};
  }
  if (
    absChange >
    dynThreshold(
      TH.inaccuracy_min,
      TH.inaccuracy_pct_500_1000,
      TH.inaccuracy_pct_gt_1000,
      opts.lastScore,
    )
  ) {
    return {inaccuracy: 1, mistake: 0, blunder: 0};
  }
  return {...ZERO_CLASSIFICATION};
}

export function accuracy(
  inacc: number,
  mist: number,
  blun: number,
  totalMoves: number,
): number {
  if (!totalMoves) {
    return 100;
  }
  const raw =
    100 - ((blun * 2.5 + mist * 1.0 + inacc * 0.5) / totalMoves) * 100;
  return Math.max(50, Math.min(raw, 100));
}

export function moveAccuracyFromLoss(cpLoss: number): number {
  const loss = Math.max(0, Math.min(Number(cpLoss) || 0, 1200));
  return Math.max(0, Math.min(100, 100 * Math.exp(-loss / 450)));
}

export function accuracyFromLosses(losses: number[]): number {
  if (!losses.length) {
    return 100;
  }
  const total = losses.reduce(
    (sum, loss) => sum + moveAccuracyFromLoss(loss),
    0,
  );
  return Math.max(0, Math.min(100, total / losses.length));
}

// --- Lichess-style accuracy -------------------------------------------------
// Implements the Lichess game-accuracy model (https://lichess.org/page/accuracy):
//   1. centipawns -> win% (the side-to-move's winning chances)
//   2. per-move accuracy% from the win% the mover gave up
//   3. game accuracy per side = mean( volatility-weighted mean , harmonic mean )
// Constants below are Lichess's published values. Unlike the centipawn-loss
// model above, accuracy is computed in win% space, so the same centipawn swing
// matters far more in a balanced position than in an already-decided one.

const WIN_PERCENT_K = -0.00368208; // cp -> winning chances sigmoid
const ACC_A = 103.1668; // per-move accuracy curve
const ACC_B = -0.04354;
const ACC_C = 3.1669;

/** Centipawns (side-to-move POV) -> win% in [0,100]. */
export function winPercentFromCp(cp: number): number {
  const winningChances = 2 / (1 + Math.exp(WIN_PERCENT_K * cp)) - 1;
  return 50 + 50 * Math.max(-1, Math.min(1, winningChances));
}

/** Per-move accuracy% from the mover's win% before/after the move. */
export function moveAccuracyPercent(
  winBeforeMover: number,
  winAfterMover: number,
): number {
  // Win% the mover gave up; gains clamp to 0 -> ~100% accuracy.
  const lost = Math.max(0, winBeforeMover - winAfterMover);
  const raw = ACC_A * Math.exp(ACC_B * lost) - ACC_C;
  return Math.max(0, Math.min(100, raw));
}

function stdev(values: number[]): number {
  if (values.length < 2) {
    return 0;
  }
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  const variance =
    values.reduce((s, v) => s + (v - mean) * (v - mean), 0) / values.length;
  return Math.sqrt(variance);
}

export type LichessMove = {
  /** True if White made this move. */
  moverIsWhite: boolean;
  /** White-POV centipawn eval of the position AFTER this move. */
  cpAfterWhite: number;
};

/**
 * Lichess game accuracy for both sides from the ordered move list.
 * `startCpWhite` is the white-POV eval of the initial position (~0).
 *
 * Each side's accuracy is the mean of a volatility-weighted mean and the
 * harmonic mean of its per-move accuracies — matching Lichess, which forgives
 * imprecision in sharp/volatile positions while letting the worst moves
 * dominate the overall score.
 */
export function lichessGameAccuracies(
  moves: LichessMove[],
  startCpWhite = 0,
): {white: number; black: number} {
  if (!moves.length) {
    return {white: 100, black: 100};
  }
  // White-POV win% at every position: index 0 = start, i+1 = after move i.
  const winWhite = [startCpWhite, ...moves.map(m => m.cpAfterWhite)].map(
    winPercentFromCp,
  );

  const windowSize = Math.max(2, Math.min(8, Math.round(moves.length / 10)));

  const perMove = moves.map((move, i) => {
    const beforeWhite = winWhite[i];
    const afterWhite = winWhite[i + 1];
    const before = move.moverIsWhite ? beforeWhite : 100 - beforeWhite;
    const after = move.moverIsWhite ? afterWhite : 100 - afterWhite;
    const acc = moveAccuracyPercent(before, after);

    // Volatility weight: stdev of win% over a window of positions around the move.
    const start = Math.max(
      0,
      Math.min(i - windowSize + 2, winWhite.length - windowSize),
    );
    const weight = Math.max(
      0.5,
      Math.min(12, stdev(winWhite.slice(start, start + windowSize))),
    );
    return {moverIsWhite: move.moverIsWhite, acc, weight};
  });

  const accuracyForSide = (isWhite: boolean): number => {
    const side = perMove.filter(m => m.moverIsWhite === isWhite);
    if (!side.length) {
      return 100;
    }
    const weightSum = side.reduce((s, m) => s + m.weight, 0);
    const weightedMean =
      weightSum > 0
        ? side.reduce((s, m) => s + m.acc * m.weight, 0) / weightSum
        : side.reduce((s, m) => s + m.acc, 0) / side.length;
    const harmonicMean =
      side.length / side.reduce((s, m) => s + 1 / Math.max(m.acc, 1), 0);
    return Math.max(0, Math.min(100, (weightedMean + harmonicMean) / 2));
  };

  return {white: accuracyForSide(true), black: accuracyForSide(false)};
}

export function toUserScore(
  best1: EvalScore | null,
  stm: Color,
  userColor: Color,
): number {
  if (!best1) {
    return 0;
  }
  let stmScore: number;
  if (best1.kind === 'mate') {
    stmScore = Math.sign(best1.val) * (100000 - Math.abs(best1.val));
  } else {
    stmScore = best1.val;
  }
  const white = stm === 'w' ? stmScore : -stmScore;
  return userColor === 'w' ? white : -white;
}

export function selectBestResponses<T extends PvLine>(
  lines: T[],
  bestScoreCp: number,
  lastScore: number,
): T[] {
  const threshold = dynThreshold(
    TH.inaccuracy_min,
    TH.inaccuracy_pct_500_1000,
    TH.inaccuracy_pct_gt_1000,
    lastScore,
  );
  return lines.filter(line => bestScoreCp - line.scoreCp <= threshold);
}

export function shouldDeepen(opts: {
  player: Player;
  cls: Classification;
  scoreChange: number;
  lastScore: number;
}): boolean {
  if (opts.cls.inaccuracy || opts.cls.mistake || opts.cls.blunder) {
    return true;
  }
  const absChange = Math.abs(opts.scoreChange);
  const margin = 40;
  const thresholds = [
    dynThreshold(
      TH.inaccuracy_min,
      TH.inaccuracy_pct_500_1000,
      TH.inaccuracy_pct_gt_1000,
      opts.lastScore,
    ),
    dynThreshold(
      TH.mistake_min,
      TH.mistake_pct_500_1000,
      TH.mistake_pct_gt_1000,
      opts.lastScore,
    ),
    dynThreshold(
      TH.blunder_min,
      TH.blunder_pct_500_1000,
      TH.blunder_pct_gt_1000,
      opts.lastScore,
    ),
  ];
  return thresholds.some(
    threshold => Math.abs(threshold - absChange) <= margin,
  );
}

export function classifyPuzzle(opts: {
  player: Player;
  ply: number;
  moveIndex: number;
  cls: Classification;
  lastScore: number;
  colorToMove: Color;
  moveColor: Color;
  playedInBestResponse?: boolean;
}): {
  isPuzzle: boolean;
  type?: 'positional' | 'tactical';
  stage?: 'opening' | 'middlegame' | 'endgame';
  area?: 'mistake' | 'opportunity';
} {
  const flagged =
    opts.cls.inaccuracy === 1 ||
    opts.cls.mistake === 1 ||
    opts.cls.blunder === 1;
  if (
    opts.player !== 'me' ||
    !flagged ||
    opts.playedInBestResponse ||
    opts.lastScore <= -800
  ) {
    return {isPuzzle: false};
  }
  if (opts.ply <= 6 && !opts.cls.mistake && !opts.cls.blunder) {
    return {isPuzzle: false};
  }

  const stage =
    opts.moveIndex <= 10
      ? 'opening'
      : opts.moveIndex <= 30
      ? 'middlegame'
      : 'endgame';
  return {
    isPuzzle: true,
    type: opts.cls.inaccuracy ? 'positional' : 'tactical',
    stage,
    area: opts.moveColor !== opts.colorToMove ? 'mistake' : 'opportunity',
  };
}

export function decideBrowserEligibility(opts: {
  hasWorker: boolean;
  probeMs: number;
  cores?: number;
  deviceMemory?: number;
  plies: number;
}): {
  useBrowser: boolean;
  K: number;
  plyCap: number;
  tier: 'high' | 'mid' | 'low' | 'server';
} {
  const cores = opts.cores || 1;
  const mem = opts.deviceMemory || 0;
  if (!opts.hasWorker || opts.probeMs > 1200) {
    return {useBrowser: false, K: 0, plyCap: 0, tier: 'server'};
  }

  if (opts.probeMs < 400 && cores >= 8 && mem >= 8) {
    return {
      useBrowser: opts.plies <= 200,
      K: 4,
      plyCap: 200,
      tier: 'high',
    };
  }

  if (opts.probeMs <= 800) {
    const k = Math.max(2, Math.min(3, Math.floor(cores / 2) || 2));
    return {
      useBrowser: opts.plies <= 150,
      K: k,
      plyCap: 150,
      tier: 'mid',
    };
  }

  return {
    useBrowser: opts.plies <= 80,
    K: 1,
    plyCap: 80,
    tier: 'low',
  };
}

export function analysisCacheKey(
  gameUuid: string | number,
  engineVersion: string,
  configHash: string,
): string {
  return `browseranalysis:${gameUuid}:${engineVersion}:${configHash}`;
}
